import { useCallback, useMemo, useState } from 'react';

/**
 * Multi-select for the number-search results.
 *
 * Replaces eight loose functions that were threaded through the component
 * (toggleNumberSelection, isNumberSelected, getCurrentPageNumbers,
 * selectAllNumbers, allCurrentPageSelected, someCurrentPageSelected,
 * clearSelection, openBulkPurchaseModal) — ~82 lines of state machine mixed
 * into the render body.
 *
 * Keyed on phoneNumber because search results have no stable id.
 */
export const useNumberSelection = (pageItems = []) => {
  const [selected, setSelected] = useState([]);

  const selectedKeys = useMemo(
    () => new Set(selected.map((n) => n.phoneNumber)),
    [selected]
  );

  const isSelected = useCallback(
    (number) => selectedKeys.has(number?.phoneNumber),
    [selectedKeys]
  );

  const toggle = useCallback((number) => {
    setSelected((prev) =>
      prev.some((n) => n.phoneNumber === number.phoneNumber)
        ? prev.filter((n) => n.phoneNumber !== number.phoneNumber)
        : [...prev, number]
    );
  }, []);

  const pageAllSelected =
    pageItems.length > 0 && pageItems.every((n) => selectedKeys.has(n.phoneNumber));
  const pageSomeSelected =
    pageItems.some((n) => selectedKeys.has(n.phoneNumber)) && !pageAllSelected;

  /** Select or clear every number on the current page, leaving others alone. */
  const togglePage = useCallback(() => {
    setSelected((prev) => {
      const keys = new Set(prev.map((n) => n.phoneNumber));
      const allOn = pageItems.length > 0 && pageItems.every((n) => keys.has(n.phoneNumber));
      if (allOn) {
        const drop = new Set(pageItems.map((n) => n.phoneNumber));
        return prev.filter((n) => !drop.has(n.phoneNumber));
      }
      const additions = pageItems.filter((n) => !keys.has(n.phoneNumber));
      return [...prev, ...additions];
    });
  }, [pageItems]);

  const clear = useCallback(() => setSelected([]), []);

  return {
    selected,
    count: selected.length,
    isSelected,
    toggle,
    togglePage,
    pageAllSelected,
    pageSomeSelected,
    clear,
  };
};

export default useNumberSelection;
